import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, ArrowLeft, Mail, Lock, Eye, Users, Database, Globe, FileText, AlertCircle, Sparkles, Link2, Scale } from 'lucide-react';

const Privacy = () => {
  const sections = [
    {
      id: 'eligibility',
      title: '1. Eligibility',
      icon: Shield,
      content: 'GoalHive is only available to users 18 years and older. By using our Services, you confirm you meet this requirement.'
    },
    {
      id: 'information-collect',
      title: '2. Information We Collect',
      icon: Database,
      subsections: [
        {
          title: 'A. Information you Provide',
          items: [
            '<strong>Account Information:</strong> When you create an account, we collect your name, email address, username, password, and optional profile photo.',
            '<strong>Goal Data:</strong> Information about your goals, milestones, progress updates, and activity logs.',
            '<strong>Social Interactions:</strong> Comments, reactions, messages, or other content you share with the community.',
            '<strong>Feedback and Support:</strong> If you contact us for support or provide feedback, we collect the details you submit.'
          ]
        },
        {
          title: 'B. Information Collected Automatically',
          items: [
            '<strong>Device and Usage Data:</strong> We collect device type, operating system, app version, unique device identifiers, IP address, and analytics about your in-app activity (e.g., session duration, features used). We may infer approximate location based on your IP address or device settings.',
            '<strong>Push Notification Tokens:</strong> If you enable push notifications, we collect a device token to send you goal reminders, milestone alerts, and community updates. You can disable notifications at any time in your device settings. We do not share notification tokens with third parties or use them for advertising.',
            '<strong>Cookies and Similar Technologies:</strong> If you access any web or API-based features, we may use cookies or similar tools to enhance your experience and understand how our services are used.'
          ]
        },
        {
          title: 'C. Information from Third Parties',
          items: [
            'If you sign in using Apple or Google, we receive limited profile data (name, email, and profile image) as permitted by those platforms.',
            'We may also generate insights or recommendations about your goals based on your activity to personalize your experience.'
          ]
        }
      ]
    },
    {
      id: 'how-use',
      title: '3. How We Use Information',
      icon: Eye,
      content: 'We use your information to:',
      list: [
        'Provide, operate, and improve GoalHive.',
        'Create and manage your GoalHive account',
        'Send notifications, reminders, or updates relevant to your goals',
        'Personalize your experience and AI-powered recommendations.',
        'Enable community engagement, goal tracking, and content sharing.',
        'Communicate with you about updates, new features, or promotional offers.',
        'Maintain safety, detect fraud, and enforce our Terms of Service.',
        'Comply with legal obligations or respond to lawful requests.'
      ],
      note: '<strong>Lawful Bases:</strong> Our collection and use of personal data are based on your consent, our legitimate interests in operating and improving the app, and, when applicable, the performance of our agreement with you.'
    },
    {
      id: 'sharing',
      title: '4. Sharing of Information',
      icon: Users,
      content: 'We do not sell or rent your personal data. We may share information only with:',
      list: [
        'Service providers who support our operations (e.g., hosting, analytics, cloud storage, customer support).',
        'Legal authorities when required to comply with applicable law or lawful requests.',
        'Other users in accordance with your privacy settings (e.g., goals or posts you make public).'
      ],
      note: 'If we ever engage in "sharing" data for cross-context behavioral advertising, we will update this Policy and provide a clear opt-out mechanism.'
    },
    {
      id: 'user-content',
      title: '5. User-Generated Content',
      icon: FileText,
      content: 'You are responsible for any content you share on GoalHive. We may moderate, remove, or restrict content that violates our Community Guidelines or applicable law.'
    },
    {
      id: 'retention',
      title: '6. Data Retention',
      icon: Database,
      content: 'We retain your information as long as your account is active or as needed to provide our Services. We may also retain certain information to comply with legal obligations, resolve disputes, and enforce our agreements. You can delete your account at any time directly in the app through your account settings.',
      subsections: [
        {
          title: 'Specific Retention Periods',
          items: [
            '<strong>Active Account Data:</strong> Retained while your account is active.',
            '<strong>Deleted Account Data:</strong> Upon account deletion, your personal data is permanently deleted immediately. This action cannot be undone. However, some data may persist in backup systems for up to 30 days before being permanently removed.',
            '<strong>Device and Analytics Data:</strong> Retained for 12 months, then aggregated or deleted.',
            '<strong>DMCA/Copyright Complaints:</strong> Retained indefinitely for legal compliance.',
            '<strong>Support Tickets:</strong> Retained indefinitely for quality assurance and legal purposes.',
            '<strong>Aggregated Data:</strong> We may retain aggregated, anonymized data for analytics purposes indefinitely.'
          ]
        }
      ],
      note: '<strong>Important:</strong> Account deletion is immediate and cannot be undone. Make sure to export any data you wish to keep before deleting your account.'
    },
    {
      id: 'storage',
      title: '7. Data Storage and Transfers',
      icon: Globe,
      content: 'Your information is stored and processed in the United States, where our servers and service providers are located.',
      list: [
        'If you access GoalHive from outside the U.S. (for example, from Canada), your data will be transferred to the United States for processing and storage.',
        'When data is transferred across borders, it becomes subject to the laws of the receiving jurisdiction and may be accessible to U.S. authorities under applicable law.',
        'We take reasonable measures to protect your information during these transfers and ensure it is handled in accordance with this Privacy Policy.'
      ]
    },
    {
      id: 'security',
      title: '8. Security',
      icon: Lock,
      content: 'We use administrative, technical, and physical safeguards appropriate to the sensitivity of the data we collect. While we strive to protect your information, no system is completely secure.',
      note: '<strong>Data Breach Notification:</strong> If a data breach affects your personal information, we will notify you via email within 72 hours of discovery, including the nature of the breach and steps you can take to protect yourself.'
    },
    {
      id: 'rights',
      title: '9. Your Rights',
      icon: Shield,
      content: 'Depending on your location, you may have the right to:',
      list: [
        'Access, correct, or delete your personal data.',
        'Withdraw consent at any time (where processing is based on consent).',
        'Request a copy of your data (data portability).',
        'Opt out of marketing communications.'
      ],
      note: 'To exercise these rights, contact us at support@goalhive.com. We may need to verify your identity before fulfilling certain requests to protect your data. We will not discriminate against you for exercising any of your privacy rights.'
    },
    {
      id: 'tracking',
      title: '10. Tracking Technologies',
      icon: Eye,
      content: 'GoalHive uses analytics to improve performance and user experience. These analytics do not identify you personally or track your activity across other apps. You can limit certain data collection through your device\'s privacy settings. We do not use analytics data for advertising purposes.',
      note: '<strong>Global Privacy Control:</strong> We recognize Global Privacy Control (GPC) signals from your browser or device as a valid opt-out request for the sale or sharing of personal information.'
    },
    {
      id: 'ai',
      title: '11. AI Features',
      icon: Sparkles,
      content: 'GoalHive may use artificial intelligence ("AI") tools to provide personalized suggestions, insights, and recommendations based on the information you share (such as goals, activities, and app interactions).',
      subsections: [
        {
          title: 'Data Inputs:',
          items: ['The AI may process your text entries, activity logs, or connected app data to generate insights and recommendations.']
        },
        {
          title: 'Data Protection:',
          items: ['We do not sell your AI data or share it with third parties outside our service providers. AI tools are designed solely to improve your GoalHive experience.']
        },
        {
          title: 'Transparency:',
          items: ['AI-generated content may not always be accurate, complete, or suited to your individual circumstances.']
        },
        {
          title: 'No Sensitive Profiling:',
          items: ['GoalHive\'s AI features do not infer or make decisions about sensitive categories such as health diagnoses, political beliefs, or financial status.']
        }
      ],
      note: 'Your use of AI features is subject to both this Privacy Policy and our Terms of Service.'
    },
    {
      id: 'third-party-services',
      title: '12. Third-Party Services & Integrations',
      icon: Link2,
      content: 'We want to be transparent about what third-party services GoalHive does and does not integrate with:',
      subsections: [
        {
          title: 'Authentication Services',
          items: [
            '<strong>Apple Sign-In & Google Sign-In:</strong> We offer the option to create an account using your Apple or Google credentials. When you use these services, we receive limited profile information (name, email, profile photo) as permitted by those platforms. Your use of these services is subject to Apple\'s and Google\'s respective privacy policies.'
          ]
        },
        {
          title: 'YouTube Integration',
          items: [
            '<strong>Video Linking Feature:</strong> Users may attach motivational or educational YouTube videos to their goals. When you use this feature, we collect and store YouTube video URLs, display YouTube thumbnails via YouTube\'s public CDN (img.youtube.com), and provide an in-app browser to youtube.com to help you discover content.',
            '<strong>Data Shared with YouTube:</strong> When you use the in-app YouTube browser or watch embedded videos, YouTube may collect information about your viewing activity in accordance with YouTube\'s Privacy Policy. We transmit only the video URL to our servers - we do not send your viewing behavior or watch history to YouTube ourselves.',
            '<strong>Data We Store:</strong> We store the YouTube video URL (a text string) and video metadata (title, description you provide). This data is associated with your goals and may be visible to other users based on your privacy settings.',
            '<strong>YouTube\'s Embed Player:</strong> Videos play in YouTube\'s official iframe embed player. YouTube controls this player and may use cookies and tracking technologies as described in their privacy policy. We do not download, cache, or modify YouTube video content.',
            '<strong>Your Privacy Choices:</strong> You can control whether your YouTube-linked goals are public or private in your GoalHive privacy settings. To manage how YouTube tracks your activity, visit YouTube\'s privacy settings and Google\'s privacy controls.'
          ]
        },
        {
          title: 'Service Providers & Infrastructure',
          items: [
            '<strong>Cloud Services:</strong> We use third-party cloud service providers to host, store, and process your data, including account information, goals, progress updates, and content. These providers operate secure data centers and process data on our behalf under data protection agreements.',
            '<strong>Analytics & Support:</strong> We use service providers for app analytics, performance monitoring, and customer support. These providers help us improve our Services and respond to your inquiries.',
            '<strong>No Advertising Partners:</strong> We do not share your data with advertising networks or data brokers.'
          ]
        },
        {
          title: 'What We Do NOT Do',
          items: [
            '<strong>No Unauthorized Downloads:</strong> We do not download, convert, cache, or extract content from YouTube or other video platforms. We only save URL references (text strings).',
            '<strong>No Premium Service Access:</strong> We do not provide unauthorized access to premium services like Spotify, Apple Music, Netflix, or other paid streaming platforms.',
            '<strong>User Content:</strong> All other media content on GoalHive (photos, videos) is original user-generated content uploaded by users for tracking their personal goals and progress.'
          ]
        }
      ],
      note: '<strong>Third-Party Privacy Policies:</strong> When you use YouTube integration, your activity may be subject to YouTube\'s Privacy Policy (<a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" class="text-[#0a0a0a] hover:text-[#666] underline transition-colors">policies.google.com/privacy</a>). For complete details on content usage and intellectual property policies, please see our <a href="/terms" class="text-[#0a0a0a] hover:text-[#666] underline transition-colors">Terms of Service</a>.'
    },
    {
      id: 'children',
      title: '13. Children\'s Privacy',
      icon: AlertCircle,
      content: 'GoalHive is not directed to individuals under 18. We do not knowingly collect personal data from minors. If we learn we have inadvertently collected such information, we will delete it promptly.'
    },
    {
      id: 'updates',
      title: '14. Updates to this Policy',
      icon: FileText,
      content: 'We may update this Privacy Policy from time to time. Updates will be posted in the app and on our website, with the "Effective Date" revised accordingly.'
    },
    {
      id: 'ccpa',
      title: '15. California Privacy Rights',
      icon: Scale,
      content: 'If you are a California resident, the California Consumer Privacy Act ("CCPA") provides you with specific rights regarding your personal information.',
      subsections: [
        {
          title: 'A. Categories of Personal Information We Collect',
          items: [
            '<strong>Identifiers:</strong> Name, email address, username, device identifiers, IP address',
            '<strong>Personal Information:</strong> Name, email address, profile photo',
            '<strong>Internet/Network Activity:</strong> App usage, session data, device type, operating system',
            '<strong>Geolocation Data:</strong> Approximate location based on IP address (not precise GPS)',
            '<strong>Audio/Visual Information:</strong> Profile photos, user-uploaded photos and videos',
            '<strong>Inferences:</strong> AI-generated insights and recommendations about your interests',
            '<strong>Sensitive Personal Information:</strong> Account login credentials (encrypted passwords)'
          ]
        },
        {
          title: 'B. Your CCPA Rights',
          items: [
            '<strong>Right to Know:</strong> Request disclosure of the categories and specific pieces of personal information we have collected about you.',
            '<strong>Right to Delete:</strong> Request deletion of your personal information, subject to certain exceptions.',
            '<strong>Right to Correct:</strong> Request correction of inaccurate personal information.',
            '<strong>Right to Non-Discrimination:</strong> We will not discriminate against you for exercising any of your CCPA rights.'
          ]
        },
        {
          title: 'C. How to Exercise Your Rights',
          items: [
            '<strong>Email:</strong> support@goalhive.com',
            '<strong>Subject Line:</strong> "CCPA Request - [Right to Know / Delete / Correct]"',
            '<strong>Include:</strong> Your name, email address, username, and description of your request',
            '<strong>Verification:</strong> We will verify your identity using account login and email verification before processing your request.',
            '<strong>Response Time:</strong> We will respond within 45 days, or notify you if we need additional time (up to 90 days total).'
          ]
        }
      ],
      note: '<strong>Authorized Agents:</strong> You may designate an authorized agent to make a request on your behalf. See Section 16 for instructions. <strong>No Sale or Sharing:</strong> We do not sell or share personal information for cross-context behavioral advertising.'
    },
    {
      id: 'authorized-agent',
      title: '16. Authorized Agent Process',
      icon: Users,
      content: 'California residents may use an authorized agent to submit privacy requests (such as requests to know, delete, or correct personal information) on their behalf.',
      subsections: [
        {
          title: 'Requirements for Authorized Agents',
          items: [
            '<strong>Proof of Authorization:</strong> Submit a valid power of attorney under California Probate Code sections 4000-4465, OR a signed written authorization from the consumer.',
            '<strong>Consumer Verification:</strong> The consumer must verify their identity directly with us (via email or account login), OR directly confirm they provided you permission.',
            '<strong>Agent Verification:</strong> You must verify your identity by providing your full name, email, phone number, and relationship to the consumer.'
          ]
        },
        {
          title: 'How to Submit',
          items: [
            '<strong>Email:</strong> support@goalhive.com',
            '<strong>Subject Line:</strong> "CCPA Authorized Agent Request"',
            '<strong>Include:</strong> Proof of authorization, consumer\'s name/email/username, your name/email/relationship, and type of request (Know, Delete, or Correct)',
            '<strong>Processing Time:</strong> We will respond within 45 days, or notify you if we need additional time (up to 90 days total).'
          ]
        }
      ],
      note: 'We reserve the right to deny a request from an authorized agent if we cannot verify that the agent is authorized to act on the consumer\'s behalf.'
    },
    {
      id: 'contact',
      title: '17. Contact Us',
      icon: Mail,
      content: '<strong>Data Controller:</strong> GoalHive LLC<br/><strong>Location:</strong> Delaware, USA<br/><strong>General Inquiries:</strong> <a href="mailto:support@goalhive.com" class="text-[#0a0a0a] hover:text-[#666] underline transition-colors">support@goalhive.com</a><br/><strong>DMCA/Copyright:</strong> <a href="mailto:dmca@goalhive.com" class="text-[#0a0a0a] hover:text-[#666] underline transition-colors">dmca@goalhive.com</a>'
    }
  ];

  return (
    <div className="min-h-screen bg-[#fafafa] text-[#0a0a0a]">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-[#e5e5e5]/50">
        <div className="mx-auto max-w-7xl px-6">
          <div className="flex h-16 items-center justify-between">
            <Link to="/" className="flex items-center gap-2.5">
              <img src="/image.webp" alt="GoalHive" className="w-8 h-8 object-contain" />
              <span className="text-lg font-semibold tracking-tight text-[#0a0a0a]">GoalHive</span>
            </Link>
            <div className="hidden md:flex items-center">
              <div className="flex items-center gap-1 p-1 bg-[#f5f5f5] rounded-full">
                <Link to="/support" className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#0a0a0a] hover:bg-white rounded-full transition-all duration-200">Support</Link>
                <Link to="/privacy" className="px-4 py-2 text-sm font-medium bg-white text-[#0a0a0a] rounded-full shadow-sm transition-all duration-200">Privacy</Link>
                <Link to="/terms" className="px-4 py-2 text-sm font-medium text-[#666] hover:text-[#0a0a0a] hover:bg-white rounded-full transition-all duration-200">Terms</Link>
              </div>
            </div>
            <Link
              to="/"
              className="h-10 px-5 bg-[#0a0a0a] text-white text-sm font-medium rounded-full hover:bg-[#171717] hover:scale-105 transition-all duration-200 shadow-lg shadow-black/10 flex items-center gap-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-14 h-14 bg-[#0a0a0a] rounded-2xl flex items-center justify-center">
              <Shield className="w-7 h-7 text-white" />
            </div>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold tracking-tight mb-6">
            <span className="text-[#0a0a0a]">Privacy </span>
            <span className="text-[#0a0a0a]">Policy</span>
          </h1>
          <p className="text-xl text-[#666] mb-4">
            Your privacy matters to us. Here's how we protect your data.
          </p>
          <p className="text-sm text-[#999]">
            <strong className="text-[#0a0a0a]">Effective Date:</strong> February 1, 2025
          </p>
        </div>
      </section>

      {/* Introduction */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6">
          <div className="p-8 rounded-2xl bg-white border border-[#e5e5e5]">
            <p className="text-lg text-[#666] leading-relaxed">
              Welcome to GoalHive ("we," "our," or "us"). Your privacy is important to us. This Privacy Policy explains how we collect, use, disclose, and protect your information when you use the GoalHive mobile application ("App") and related services.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="pb-16">
        <div className="max-w-4xl mx-auto px-6 space-y-6">
          {sections.map((section) => {
            const IconComponent = section.icon;
            return (
              <div key={section.id} id={section.id} className="scroll-mt-24">
                <div className="rounded-2xl p-8 bg-white border border-[#e5e5e5] hover:border-[#d4d4d4] hover:shadow-lg transition-all duration-300">
                  {/* Section Header */}
                  <div className="flex items-start space-x-4 mb-6">
                    <div className="w-12 h-12 bg-[#0a0a0a] rounded-xl flex items-center justify-center flex-shrink-0">
                      <IconComponent className="w-6 h-6 text-white" />
                    </div>
                    <div className="flex-1">
                      <h2 className="text-2xl font-bold text-[#0a0a0a] mb-4">
                        {section.title}
                      </h2>

                      {/* Main Content */}
                      {section.content && (
                        <div
                          className="text-[#666] leading-relaxed mb-4"
                          dangerouslySetInnerHTML={{ __html: section.content }}
                        />
                      )}

                      {/* List Items */}
                      {section.list && (
                        <ul className="space-y-3 mb-4">
                          {section.list.map((item, i) => (
                            <li key={i} className="flex items-start space-x-3">
                              <div className="w-1.5 h-1.5 bg-[#0a0a0a] rounded-full mt-2 flex-shrink-0"></div>
                              <span className="text-[#666]" dangerouslySetInnerHTML={{ __html: item }} />
                            </li>
                          ))}
                        </ul>
                      )}

                      {/* Subsections */}
                      {section.subsections && (
                        <div className="space-y-4 mt-4">
                          {section.subsections.map((subsection, i) => (
                            <div key={i} className="bg-[#fafafa] rounded-xl p-6 border border-[#e5e5e5]">
                              <h3 className="text-lg font-semibold text-[#0a0a0a] mb-3">{subsection.title}</h3>
                              <ul className="space-y-2">
                                {subsection.items.map((item, j) => (
                                  <li key={j} className="flex items-start space-x-3">
                                    <div className="w-1 h-1 bg-[#0a0a0a] rounded-full mt-2 flex-shrink-0"></div>
                                    <span className="text-[#666] text-sm" dangerouslySetInnerHTML={{ __html: item }} />
                                  </li>
                                ))}
                              </ul>
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Note */}
                      {section.note && (
                        <div className="bg-[#f5f5f5] border-l-4 border-[#0a0a0a] rounded-r-lg p-4 mt-4">
                          <p className="text-sm text-[#666]" dangerouslySetInnerHTML={{ __html: section.note }} />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <div className="p-12 rounded-3xl bg-[#0a0a0a]">
            <div className="w-16 h-16 bg-white/10 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <Mail className="w-8 h-8 text-white" />
            </div>
            <h3 className="text-3xl font-bold mb-4 text-white">
              Questions About Privacy?
            </h3>
            <p className="text-lg mb-8 text-white/70">
              We're here to help. Contact us anytime about your privacy concerns.
            </p>
            <a
              href="mailto:support@goalhive.com"
              className="inline-block px-8 py-4 bg-white text-[#0a0a0a] font-semibold rounded-full hover:bg-[#f5f5f5] hover:scale-105 transition-all duration-200 shadow-lg"
            >
              Contact Us
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#e5e5e5]">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2.5">
              <img src="/image.webp" alt="GoalHive" className="w-8 h-8 object-contain" />
              <div>
                <span className="text-lg font-semibold tracking-tight text-[#0a0a0a]">GoalHive</span>
                <p className="text-[#999] text-sm">&copy; 2025 GoalHive LLC. All rights reserved.</p>
              </div>
            </div>
            <div className="flex items-center gap-6 text-sm text-[#666]">
              <Link to="/support" className="hover:text-[#0a0a0a] transition-colors">Support</Link>
              <Link to="/terms" className="hover:text-[#0a0a0a] transition-colors">Terms of Service</Link>
              <Link to="/" className="hover:text-[#0a0a0a] transition-colors">Home</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Privacy;
